/**
 * BookSlotDialog Component
 * Confirmation dialog for booking a consultation slot
 * VERSION: ERROR-FIX-v3
 */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { AccessTime, Person, CalendarToday } from '@mui/icons-material';
import {
  closeBookingDialog,
  setUserNotes,
  createBooking,
  resetBookingStatus
} from '../../store/slices/bookingSlice';
import { formatSlotTime } from '../../utils/slotUtils';
import { formatDateForDisplay } from '../../utils/dateUtils';

const BookSlotDialog = () => {
  const dispatch = useDispatch();
  const {
    bookingDialogOpen,
    selectedSlot,
    userNotes,
    bookingStatus,
    error
  } = useSelector(state => state.booking);

  const handleClose = () => {
    dispatch(closeBookingDialog());
    dispatch(resetBookingStatus());
  };

  const handleNotesChange = (event) => {
    dispatch(setUserNotes(event.target.value));
  };

  const handleConfirm = () => {
    if (!selectedSlot) return;

    console.log('[BookSlotDialog] Confirming booking:', {
      slotId: selectedSlot.ID,
      userNotes
    });

    dispatch(createBooking({
      slotId: selectedSlot.ID,
      userNotes
    }));
  };

  if (!selectedSlot) return null;

  console.log('[BookSlotDialog] VERSION: ERROR-FIX-v3');
  console.log('[BookSlotDialog] bookingStatus:', bookingStatus);
  console.log('[BookSlotDialog] error:', error);
  console.log('[BookSlotDialog] selectedSlot:', selectedSlot);
  console.log('[BookSlotDialog] selectedSlot.id:', selectedSlot?.id);

  const timeDisplay = formatSlotTime(selectedSlot.start_time, selectedSlot.end_time);
  const dateDisplay = formatDateForDisplay(selectedSlot.date);
  const isLoading = bookingStatus === 'loading';

  return (
    <Dialog
      open={bookingDialogOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h5">
          Confirm Booking
        </Typography>
      </DialogTitle>

      <DialogContent>
        {bookingStatus === 'error' && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error?.message || error?.error || (typeof error === 'string' ? error : 'Failed to create booking. Please try again.')}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            You are about to book a consultation with the following details:
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <CalendarToday sx={{ mr: 2, color: 'primary.main' }} />
          <Box>
            <Typography variant="caption" color="textSecondary">
              Date
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {dateDisplay}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AccessTime sx={{ mr: 2, color: 'primary.main' }} />
          <Box>
            <Typography variant="caption" color="textSecondary">
              Time
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {timeDisplay}
            </Typography>
          </Box>
        </Box>

        {selectedSlot.astrologer_name && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Person sx={{ mr: 2, color: 'primary.main' }} />
            <Box>
              <Typography variant="caption" color="textSecondary">
                Astrologer
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {selectedSlot.astrologer_name}
              </Typography>
            </Box>
          </Box>
        )}

        <TextField
          label="Notes (Optional)"
          multiline
          rows={4}
          fullWidth
          value={userNotes}
          onChange={handleNotesChange}
          placeholder="Add any questions or topics you'd like to discuss..."
          disabled={isLoading}
          sx={{ mb: 2 }}
        />

        <Typography variant="caption" color="textSecondary">
          Duration: 15 minutes
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isLoading ? 'Booking...' : 'Confirm Booking'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BookSlotDialog;
